import React, { useState } from 'react';
import {
  Button as MuiButton,
  CircularProgress,
  Icon,
  IconButton as MuiIconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { get } from 'global/lib/lodash';

export const Button = (props) => {
  const { loading, startIcon, icon, variant = 'contained', color = 'secondary', sx, disabled, toolTip, ...rest } = props;
  let [_loading, setLoading] = useState(false);
  const isLoading = loading || _loading;

  const onClick = async (e) => {
    if (_loading) {
      return;
    }
    try {
      _loading = true;
      setLoading(true);
      props.onClick && (await props.onClick(e));
    } finally {
      _loading = false;
      setLoading(false);
    }
  };

  const ico = (() => {
    if (isLoading && (startIcon || icon)) {
      return <CircularProgress size={12} color="inherit" />;
    }
    const icoobj = startIcon || icon;
    if (!icoobj) {
      return null;
    }
    if (typeof icoobj === 'string') {
      return <Icon>{icoobj}</Icon>;
    }
    return icoobj;
  })();

  const Comp = (
    <MuiButton
      {...rest}
      color={color}
      variant={variant}
      disabled={disabled || isLoading}
      {...(ico ? { startIcon: ico } : {})}
      onClick={onClick}
      sx={{
        ...(!(props.title || props.children) && { '& .MuiButton-startIcon, & .MuiButton-endIcon': { mx: 0 } }),
        ...sx,
      }}>
      {props.title || props.children}
      {isLoading && !startIcon && !icon && <CircularProgress size={12} />}
    </MuiButton>
  );

  if (toolTip) {
    return (
      <Tooltip title={toolTip}>
        <span>{Comp}</span>
      </Tooltip>
    );
  }

  return Comp;
};

export const IconButton = (props) => {
  const [_loading, setLoading] = useState(false);
  const { loading, startIcon, icon, loadingProps, variant = 'contained', color = 'secondary', toolTip, sx, disabled, ...rest } = props;
  const filled = (variant === 'contained' || variant === 'filled') && !disabled && color !== 'inherit' && color !== 'default';

  const onClick = async (e) => {
    try {
      setLoading(true);
      props.onClick && (await props.onClick(e));
    } finally {
      setLoading(false);
    }
  };

  const ico = (() => {
    const icoobj = startIcon || icon;
    if (typeof icoobj === 'string') {
      return <Icon>{icoobj}</Icon>;
    }
    if (icoobj) {
      return icoobj;
    }
    return props.children;
  })();

  const Comp = (
    <MuiIconButton
      {...rest}
      disabled={disabled}
      color={filled ? 'default' : color}
      sx={[
        filled && {
          bgcolor: `${color}.main`,
          color: `${color}.contrastText`,
          '&:hover': { bgcolor: `${color}.dark` },
          '& .MuiCircularProgress-root': { color: `${color}.contrastText` },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      onClick={onClick}>
      {loading || _loading ? <CircularProgress size={12} {...(loadingProps || {})} /> : ico}
    </MuiIconButton>
  );

  if (toolTip) {
    return (
      <Tooltip title={toolTip}>
        <span>{Comp}</span>
      </Tooltip>
    );
  }

  return Comp;
};

export const ButtonDrop = (props) => {
  const { startIcon, ...rest } = props;
  const [state, setState] = useState({
    anchor: null,
  });

  const onClick = (e) => {
    setState((s) => ({ ...s, anchor: e.currentTarget }));
  };

  const onClose = () => {
    setState((s) => ({ ...s, anchor: null }));
  };

  const onSelect = (opt) => {
    props.onSelect && props.onSelect(opt);
    onClose();
  };

  const opts = get(props, 'options', []);

  return (
    <>
      <Button {...rest} onClick={onClick} />
      <Menu id="simple-menu" anchorEl={state.anchor} keepMounted open={Boolean(state.anchor)} onClose={onClose}>
        {opts.map((opt, i) => {
          const { disabled, value, dense, divider, selected, autoFocus, ...domainOnly } = opt;
          return (
            <MenuItem
              key={i}
              disabled={disabled}
              value={value}
              dense={dense}
              divider={divider}
              selected={selected}
              autoFocus={autoFocus}
              onClick={() => onSelect(opt)}>
              {opt.title || opt.children}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export const LinkButton = (props) => {
  return (
    <Button {...props} variant="text" color="secondary" sx={{ textTransform: 'none' }}>
      {props.children}
    </Button>
  );
};
